import React, { useState, useEffect, useCallback } from 'react';
import { activityLogApi, unitsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  History as HistoryIcon, User, Filter, RotateCw, Shield, ChevronDown, ChevronUp,
} from 'lucide-react';

const CATEGORY_STYLE = {
  bank_recon:  { label: 'Bank',        bg: 'bg-blue-50',   text: 'text-blue-700',   ring: 'ring-blue-200' },
  load:        { label: 'Load',        bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  deduction:   { label: 'Deduction',   bg: 'bg-amber-50',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  settlement:  { label: 'Settlement',  bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-200' },
  accounting:  { label: 'Accounting',  bg: 'bg-slate-100', text: 'text-slate-700',  ring: 'ring-slate-200' },
  driver:      { label: 'Driver',      bg: 'bg-pink-50',   text: 'text-pink-700',   ring: 'ring-pink-200' },
  unit:        { label: 'Unit',        bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200' },
  other:       { label: 'Other',       bg: 'bg-slate-100', text: 'text-slate-600',  ring: 'ring-slate-200' },
};

// ─── Friendly meta rendering ────────────────────────────────────────────────
// Backend stores `meta` as a compact dict. The raw JSON blob is unreadable to
// non-technical users, so we translate keys to labels + humanise values.
const META_KEY_LABELS = {
  amount: 'Amount',
  net_payout: 'Net payout',
  dispatch_fee: 'Dispatch fee',
  broker: 'Broker',
  broker_name: 'Broker',
  category: 'Category',
  subcategory: 'Subcategory',
  unit_id: 'Unit',
  unit_number: 'Unit',
  week_start: 'Week',
  date: 'Date',
  frequency: 'Frequency',
  unit_count: 'Units targeted',
  sent: 'Emails sent',
  failed: 'Emails failed',
  recipient: 'Recipient',
  op: 'Operation',
  value: 'New value',
  count: 'Affected rows',
  changes: 'Changed fields',
};

const humaniseKey = (k) =>
  META_KEY_LABELS[k]
  || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isMoneyKey = (k) =>
  ['amount', 'net_payout', 'dispatch_fee', 'payout_amount', 'total', 'gross', 'net']
    .some((suffix) => k.toLowerCase().endsWith(suffix));

const isDateLikeKey = (k) => /(week_start|date|_at)$/i.test(k);

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(v); }
};

const fmtValue = (k, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length === 0 ? '—' : `${v.length} item${v.length === 1 ? '' : 's'}`;
  if (typeof v === 'object') {
    // For nested change diffs like {field: {from, to}} — render as X → Y
    if ('from' in v && 'to' in v) return `${fmtValue(k, v.from)} → ${fmtValue(k, v.to)}`;
    // Fallback: render inline JSON but shorter
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  if (isMoneyKey(k) && typeof v !== 'string') return fmtMoney(v);
  if (isDateLikeKey(k) && typeof v === 'string' && v.length >= 8) return fmtDate(v);
  return String(v);
};

function FriendlyMeta({ meta, unitMap = {} }) {
  const entries = Object.entries(meta || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');

  const resolveUnit = (v) => {
    if (v === 'COMPANY') return 'Company';
    return unitMap[v] || v;
  };

  const renderValue = (k, v) => {
    if (k === 'unit_id' || k === 'to' || k === 'from') {
      if (typeof v === 'string' && (v === 'COMPANY' || unitMap[v])) return resolveUnit(v);
    }
    return fmtValue(k, v);
  };

  // Special-case: "changes" nested dict — render inline as "Field: from → to"
  const changesEntry = entries.find(([k]) => k === 'changes');
  const otherEntries = entries.filter(([k]) => k !== 'changes');

  if (entries.length === 0) {
    return <div className="text-xs text-muted-foreground italic">No additional details.</div>;
  }
  return (
    <div className="mt-1.5 space-y-1.5">
      {otherEntries.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px]">
          {otherEntries.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-muted-foreground">{humaniseKey(k)}</dt>
              <dd className="font-medium text-slate-700 break-words">{renderValue(k, v)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {changesEntry && (
        <div>
          <div className="text-[11px] text-muted-foreground mb-0.5">Changes</div>
          <ul className="space-y-0.5 text-[11px]">
            {Object.entries(changesEntry[1] || {}).map(([field, val]) => {
              const from = val?.from;
              const to = val?.to;
              const isUnit = field === 'unit_id';
              return (
                <li key={field} className="rounded bg-slate-50 px-2 py-1">
                  <span className="font-medium text-slate-700">{humaniseKey(field)}: </span>
                  <span className="text-slate-500">{isUnit ? resolveUnit(from) : fmtValue(field, from)}</span>
                  <span className="mx-1 text-slate-400">→</span>
                  <span className="text-blue-700 font-medium">{isUnit ? resolveUnit(to) : fmtValue(field, to)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

/**
 * ActivityLogPanel — reusable audit-feed component.
 *
 * Props:
 *  - variant: 'full' | 'compact'
 *    - full: History page — filters + long list (admin-gated by backend)
 *    - compact: side panel under Bank Reconciliation — no filters, short list
 *  - source: 'all' | 'bank_recon'   backend endpoint selector
 *  - initialCategory, initialDays: optional preselected filters
 *  - showHeader: bool, default true
 *  - onError: fn(err)
 */
export default function ActivityLogPanel({
  variant = 'full',
  source = 'all',
  initialCategory = '',
  initialDays = 30,
  showHeader = true,
  onError,
}) {
  const [events, setEvents] = useState([]);
  const [actors, setActors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unitMap, setUnitMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const [category, setCategory] = useState(initialCategory);
  const [actor, setActor] = useState('');
  const [days, setDays] = useState(initialDays);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({}); // per-row meta expand

  const load = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      let r;
      if (source === 'bank_recon') {
        r = await activityLogApi.bankRecon({ days, limit: variant === 'compact' ? 30 : 100 });
        setEvents(r.data.events || []);
      } else {
        r = await activityLogApi.list({
          days,
          category: category || undefined,
          actor_email: actor || undefined,
          limit: variant === 'compact' ? 30 : 200,
        });
        setEvents(r.data.events || []);
        setActors(r.data.actors || []);
        setCategories(r.data.categories || []);
      }
    } catch (e) {
      if (e.response?.status === 403) setPermissionDenied(true);
      else onError?.(e);
    } finally {
      setLoading(false);
    }
  }, [source, days, category, actor, variant, onError]);

  useEffect(() => { load(); }, [load]);

  // Load unit map once so activity meta can show "Unit 12" instead of a UUID
  useEffect(() => {
    let cancelled = false;
    unitsApi.getAll().then((r) => {
      if (cancelled) return;
      const arr = Array.isArray(r.data) ? r.data : (r.data?.units || []);
      const m = {};
      arr.forEach((u) => { m[u.id] = u.unit_number; });
      m.COMPANY = 'Company';
      setUnitMap(m);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Client-side search across summary/actor
  const filtered = events.filter((ev) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return ((ev.summary || '').toLowerCase().includes(s)
      || (ev.actor_email || '').toLowerCase().includes(s)
      || (ev.actor_name || '').toLowerCase().includes(s));
  });

  if (permissionDenied) {
    return (
      <Card className="border-slate-200" data-testid="activity-log-forbidden">
        <CardContent className="p-4 flex items-center gap-3">
          <Shield className="h-4 w-4 text-slate-500" />
          <div className="text-sm text-muted-foreground">
            Only admins can view the full activity log.
          </div>
        </CardContent>
      </Card>
    );
  }

  const isCompact = variant === 'compact';
  return (
    <Card className={isCompact ? 'border-slate-200' : ''} data-testid={`activity-log-panel-${variant}`}>
      {showHeader && (
        <CardHeader className={isCompact ? 'p-3 pb-2' : 'pb-3'}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className={`flex items-center gap-2 ${isCompact ? 'text-base' : 'text-lg'} font-manrope`}>
              <HistoryIcon className="h-4 w-4 text-blue-600" />
              Activity {isCompact ? '' : 'History'}
              <Badge variant="outline" className="text-[10px]">{filtered.length}</Badge>
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="activity-refresh-btn">
              <RotateCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {!isCompact && (
            <div className="flex flex-wrap gap-2 mt-3">
              <div className="relative flex-1 min-w-[200px]">
                <Filter className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search summary / actor…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid="activity-search"
                />
              </div>
              <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="activity-category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_STYLE[c]?.label || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={actor || 'all'} onValueChange={(v) => setActor(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="activity-actor-filter">
                  <SelectValue placeholder="Actor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {actors.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="activity-days-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="365">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>
      )}
      <CardContent className={isCompact ? 'p-0 pt-0' : 'p-0'}>
        {loading && (
          <div className="p-4 text-xs text-muted-foreground animate-pulse">Loading activity…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <HistoryIcon className="h-5 w-5 mx-auto mb-2 opacity-40" />
            No activity in this window.
          </div>
        )}
        <div className={`divide-y ${isCompact ? 'max-h-[320px] overflow-y-auto' : ''}`}>
          {filtered.map((ev) => {
            const cat = CATEGORY_STYLE[ev.category] || CATEGORY_STYLE.other;
            const hasMeta = ev.meta && Object.keys(ev.meta).length > 0;
            const isOpen = !!expanded[ev.id];
            return (
              <div key={ev.id}
                   className="px-3 sm:px-4 py-2 hover:bg-muted/40 transition-colors"
                   data-testid={`activity-row-${ev.id}`}>
                <div className="flex items-start gap-2">
                  <span className={`shrink-0 mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 ring-inset ${cat.bg} ${cat.text} ${cat.ring}`}>
                    {cat.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{ev.summary}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span className="truncate">{ev.actor_name || ev.actor_email || 'system'}</span>
                      <span>·</span>
                      <span title={ev.created_at}>{fmtWhen(ev.created_at)}</span>
                      {hasMeta && (
                        <button
                          type="button"
                          className="ml-auto inline-flex items-center gap-0.5 text-blue-600 hover:underline"
                          onClick={() => setExpanded((s) => ({ ...s, [ev.id]: !isOpen }))}
                          data-testid={`activity-meta-toggle-${ev.id}`}
                        >
                          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          details
                        </button>
                      )}
                    </div>
                    {hasMeta && isOpen && (
                      <FriendlyMeta meta={ev.meta} unitMap={unitMap} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
